import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FileSpreadsheet,
  FileText,
  Loader2,
  Download,
  Sparkles,
  Skull,
  AlertTriangle,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import catalog from "@/api/catalog";

const SCOPES = [
  { value: "dead", icon: Skull, tone: "text-red-500" },
  { value: "no_sales", icon: AlertTriangle, tone: "text-amber-500" },
  { value: "old", icon: Calendar, tone: "text-blue-400" },
];

const GROUPS = ["vendor", "category", "product_type"];

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

/**
 * Modal that drives POST /api/catalog/liquidation-list.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - initialScope: "dead" | "no_sales" | "old"
 *  - lang: "es" | "en"
 */
export default function LiquidationDialog({
  open,
  onOpenChange,
  initialScope = "dead",
  lang = "es",
}) {
  const { t, i18n } = useTranslation();
  const effectiveLang = (lang || i18n.language || "es").startsWith("en") ? "en" : "es";

  const [scope, setScope] = useState(initialScope);
  const [format, setFormat] = useState("xlsx");
  const [groupBy, setGroupBy] = useState("vendor");
  const [minCapital, setMinCapital] = useState("0");
  const [includeImages, setIncludeImages] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sync the active scope whenever the parent opens the dialog from a
  // different KPI card.
  React.useEffect(() => {
    if (open) setScope(initialScope);
  }, [open, initialScope]);

  const isPdf = format === "pdf";

  const handleGenerate = async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const minCapNum = Math.max(0, Number(minCapital) || 0);
      const res = await catalog.liquidationList({
        scope,
        format,
        group_by: groupBy,
        min_capital_per_group: minCapNum,
        include_images: isPdf && includeImages,
        lang: effectiveLang,
      });
      // Filename from Content-Disposition if available.
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(disp);
      const fname = match ? match[1] : `liquidation_${scope}.${format}`;
      const aiSource = res.headers?.["x-liquidation-ai-source"] || "fallback";
      const totalSkus = parseInt(res.headers?.["x-liquidation-total-skus"] || "0", 10);
      const bytes = parseInt(res.headers?.["x-liquidation-bytes"] || "0", 10);
      const sizeKb = Math.max(1, Math.round(bytes / 1024));
      const elapsedSec = ((performance.now() - t0) / 1000).toFixed(1);
      downloadBlob(res.data, fname);
      toast.success(
        t("liquidation.success", {
          skus: totalSkus.toLocaleString(),
          size: sizeKb,
          elapsed: elapsedSec,
          ai: aiSource,
        })
      );
      onOpenChange?.(false);
    } catch (err) {
      console.error(err);
      toast.error(t("liquidation.failure"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="liquidation-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {t("liquidation.title")}
          </DialogTitle>
          <DialogDescription>{t("liquidation.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Scope */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("liquidation.scope")}
            </Label>
            <div className="grid grid-cols-3 gap-1.5">
              {SCOPES.map(({ value, icon: Icon, tone }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScope(value)}
                  data-testid={`liquidation-scope-${value}`}
                  className={`flex flex-col items-center gap-1 rounded-md border p-2 text-[11px] transition ${
                    scope === value
                      ? "border-primary/60 bg-primary/10"
                      : "border-border bg-card hover:border-primary/40"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${tone}`} />
                  {t(`catalog.views.${value}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Format */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("liquidation.format")}
            </Label>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => setFormat("xlsx")}
                data-testid="liquidation-format-xlsx"
                className={`flex items-center justify-center gap-1.5 rounded-md border p-2 text-xs transition ${
                  format === "xlsx"
                    ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-500"
                    : "border-border bg-card hover:border-emerald-500/40"
                }`}
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excel
              </button>
              <button
                type="button"
                onClick={() => setFormat("pdf")}
                data-testid="liquidation-format-pdf"
                className={`flex items-center justify-center gap-1.5 rounded-md border p-2 text-xs transition ${
                  format === "pdf"
                    ? "border-red-500/60 bg-red-500/10 text-red-500"
                    : "border-border bg-card hover:border-red-500/40"
                }`}
              >
                <FileText className="h-4 w-4" />
                PDF
              </button>
            </div>
          </div>

          {/* Group by */}
          <div className="space-y-1.5">
            <Label
              htmlFor="liq-group"
              className="text-xs uppercase tracking-wide text-muted-foreground"
            >
              {t("liquidation.groupBy")}
            </Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger id="liq-group" className="h-8 text-xs" data-testid="liquidation-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUPS.map((g) => (
                  <SelectItem key={g} value={g}>
                    {t(`liquidation.groups.${g}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Min capital */}
          <div className="space-y-1.5">
            <Label
              htmlFor="liq-min"
              className="text-xs uppercase tracking-wide text-muted-foreground"
            >
              {t("liquidation.minCapital")}
            </Label>
            <Input
              id="liq-min"
              type="number"
              min="0"
              step="1000"
              value={minCapital}
              onChange={(e) => setMinCapital(e.target.value)}
              className="h-8 text-xs"
              data-testid="liquidation-min-capital"
            />
          </div>

          {/* Include images (PDF only) */}
          {isPdf && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
              <input
                type="checkbox"
                checked={includeImages}
                onChange={(e) => setIncludeImages(e.target.checked)}
                className="mt-0.5"
                data-testid="liquidation-include-images"
              />
              <span>{t("liquidation.includeImages")}</span>
            </label>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={loading}
            size="sm"
            data-testid="liquidation-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={loading}
            size="sm"
            data-testid="liquidation-generate"
          >
            {loading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                {t("liquidation.generating")}
              </>
            ) : (
              <>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {t("liquidation.generate")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
